import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Briefcase, FileText, MailOpen } from 'lucide-react';
import { useUserReadNotificationsMutation } from 'src/lib/endpoints/dashboard/dashboard.api';
import EntityAvatar from '../EntityAvatar';
import { cn } from '../ui-x';

const iconFor = (title = '') => {
  if (title.includes('Job Offer')) return <FileText className='h-4 w-4' />;
  if (title.includes('Job Match')) return <Briefcase className='h-4 w-4' />;
  if (title.includes('Job Invite')) return <MailOpen className='h-4 w-4' />;
  return <Bell className='h-4 w-4' />;
};

const NotificationCard = ({ notification }) => {
  const [isRead, setIsRead] = useState(!!notification?.is_read);
  const [toggle, setToggle] = useState(false);
  const { readNotification } = useUserReadNotificationsMutation(notification?.id);

  const markAsRead = async () => {
    setIsRead(true);
    readNotification(
      {},
      {
        onSuccess: () => {},
        onError: () => {},
      }
    );
  };

  return (
    <div className='rounded-xl border border-transparent transition-colors hover:border-border hover:bg-surface'>
      <button
        type='button'
        onClick={() => {
          markAsRead();
          setToggle((prev) => !prev);
        }}
        className='flex w-full items-start gap-3 p-3 text-left'
      >
        {notification?.sender_avatar ? (
          <EntityAvatar
            src={notification.sender_avatar}
            name={notification?.title}
          />
        ) : (
          <span className='grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-navy-100 text-brand-navy'>
            {iconFor(notification?.title)}
          </span>
        )}

        <span className='min-w-0 flex-1'>
          <span className='flex items-center gap-2'>
            {!isRead && (
              <span className='h-1.5 w-1.5 shrink-0 rounded-full bg-brand-orange' />
            )}
            <span
              className={cn(
                'truncate text-sm font-semibold',
                isRead ? 'text-muted-foreground' : 'text-brand-navy'
              )}
            >
              {notification?.title}
            </span>
          </span>

          {!toggle && (
            <span className='mt-0.5 line-clamp-2 block text-xs text-muted-foreground'>
              {notification?.body}
            </span>
          )}

          <span className='mt-1 block text-[10px] uppercase tracking-wider text-muted-foreground'>
            {formatDistanceToNow(
              new Date(notification?.created_at ?? Date.now()),
              { addSuffix: true }
            )}
          </span>
        </span>
      </button>

      {toggle && (
        <div className='px-3 pb-3 pl-[3.75rem] text-xs text-muted-foreground'>
          <p>
            {notification?.body}

            {notification?.title?.includes('New Job Offer') && (
              <Link
                to={
                  notification?.project_detail_id
                    ? `/alloffer/${notification.project_detail_id}`
                    : `/alloffer`
                }
                className='ml-1 inline font-semibold text-brand-orange hover:underline'
              >
                Click here to view the job
              </Link>
            )}

            {notification?.title?.includes('New Job Match') && (
              <Link
                to={`/jobs/${notification.job_details_id}`}
                className='ml-1 inline font-semibold text-brand-orange hover:underline'
              >
                Click here to indicate interest
              </Link>
            )}

            {notification?.title?.includes('New Job Invite') && (
              <Link
                to={
                  notification?.invite_id
                    ? `/invites/${notification.invite_id}`
                    : `/invites`
                }
                className='ml-1 inline font-semibold text-brand-orange hover:underline'
              >
                Click here to view more
              </Link>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default NotificationCard;
